// ╔══════════════════════════════════════════════════════════════════════╗
// ║  SCENARIO DATA — Replace this block to swap the entire scenario.   ║
// ║  UI code below this block does NOT need to change.                 ║
// ╚══════════════════════════════════════════════════════════════════════╝

export const EVIDENCE_TYPES = {
  'note-gap': { label: 'Missing from visit note', icon: '⊘' },
  'protocol-gap': { label: 'Protocol step missing', icon: '△' },
  'time-expiring': { label: 'Deadline approaching', icon: '◷' },
  'state-mismatch': { label: 'Needs update', icon: '≠' },
  'cadence-drift': { label: 'Overdue', icon: '↝' },
  'gate-blocked': { label: 'Blocker', icon: '⊗' },
  'safety-trigger': { label: 'Safety check', icon: '!' },
  'transition-trigger': { label: 'Transition step', icon: '⇥' },
};

export const PATIENTS = [
  {
    id: 'P001',
    name: 'Sarah Mitchell',
    age: 71,
    mrn: 'MRN-6120',
    athenaId: '612000',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Patel',
  },
  {
    id: 'P002',
    name: 'David Kowalski',
    age: 74,
    mrn: 'MRN-8835',
    athenaId: '883500',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Nakamura',
  },
  {
    id: 'P003',
    name: 'Linda Okonkwo',
    age: 68,
    mrn: 'MRN-4471',
    athenaId: '447100',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Chen',
  },
  {
    id: 'P004',
    name: 'Richard Huang',
    age: 73,
    mrn: 'MRN-2209',
    athenaId: '220900',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Patel',
  },
  {
    id: 'P005',
    name: 'Barbara Torres',
    age: 70,
    mrn: 'MRN-5587',
    athenaId: '558700',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Nakamura',
  },
  {
    id: 'P006',
    name: 'George Adams',
    age: 76,
    mrn: 'MRN-3344',
    athenaId: '334400',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Chen',
  },
];

export const VISIT_TYPES = {
  99205: {
    label: 'New Patient Visit (99205)',
    short: '99205',
    pathway: 'cognitive',
    phase: 1,
    readinessDimensions: ['scheduling', 'stakeholders'],
    description: 'First visit — intake, history, and initial plan',
  },
  99483: {
    label: 'Cognitive Assessment (99483)',
    short: '99483',
    pathway: 'cognitive',
    phase: 2,
    readinessDimensions: ['evidence', 'assessments', 'scheduling', 'stakeholders'],
    description: 'Full cognitive workup — requires labs, MRI, Cryos, and caregiver present',
  },
};

export const READINESS_DIMS = {
  evidence: {
    label: 'Test Results',
    icon: '◈',
    description: 'Labs, MRI, and biomarker results are back',
  },
  assessments: {
    label: 'Assessments',
    icon: '◇',
    description: 'Cognitive testing and caregiver questionnaires completed',
  },
  scheduling: {
    label: 'Scheduling',
    icon: '▫',
    description: 'Visit booked with the right type and timing',
  },
  stakeholders: {
    label: 'People',
    icon: '◎',
    description: 'Caregiver and family confirmed to attend',
  },
};

export const DATE_TODAY = new Date().toISOString().slice(0, 10);

export const TASKS = [
  // ═══ PATIENT: Sarah Mitchell (P001) — 99205 signed TODAY, nothing done ═══

  // Half A: Immediate gap closure (same-day)
  {
    id: 'T001',
    patientId: 'P001',
    team: 'clinical',
    title: 'Track dementia lab results — placed during visit',
    caption:
      'Labs (B12, TSH, CMP, CBC) were ordered by Dr. Patel during the 99205. Linked to readiness tracking — awaiting results.',
    role: 'MA',
    owner: 'Kim Torres',
    due: '2026-02-14',
    priority: 'medium',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    evidence: {
      type: 'state-mismatch',
      summary:
        "Dementia labs (B12, TSH, CMP, CBC) were correctly ordered during the visit — confirmed in Athena orders. Linked to Phase 2 readiness tracking automatically. Expected turnaround: 5–7 days. No action needed now — system will flag if results don't arrive by 2/10.",
      source: 'Athena orders — 99205 encounter 2/3/2026',
      sourceRef: 'ORD-20260203-P001-LABS',
    },
  },
  {
    id: 'T002',
    patientId: 'P001',
    team: 'ops',
    title: 'Get the baseline MRI ordered',
    caption: "Note says 'MRI brain ordered' but no order or referral exists in Athena.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    evidence: {
      type: 'note-gap',
      summary:
        "Dr. Patel's 99205 note says 'MRI brain ordered' but there's no imaging order or external referral in Athena. Needs to be placed today so results are back in time for the 99483.",
      source: 'Athena visit note — 99205 encounter 2/3/2026',
      sourceRef: 'ENC-20260203-P001',
    },
  },
  {
    id: 'T003',
    patientId: 'P001',
    team: 'clinical',
    title: 'Order blood biomarker panel (4-panel)',
    caption: 'Edge protocol: p-tau217, Aβ42/40, NfL, GFAP. 2026 target — ≥70% of Phase 1 patients.',
    role: 'MA',
    owner: 'Kim Torres',
    due: '2026-02-03',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'edge',
    mustHave2026: true,
    evidence: {
      type: 'protocol-gap',
      summary:
        'Edge protocol calls for a 4-panel blood biomarker test at first visit. This is a 2026 MUST-HAVE target. Order needs to be placed today.',
      source: 'Mindspan Clinical Protocol — Edge Layer',
      sourceRef: 'EDGE-BIO-001',
    },
  },
  {
    id: 'T004',
    patientId: 'P001',
    team: 'clinical',
    title: 'Order APOE genetic test',
    caption:
      'Tells us risk level for treatment side effects. Edge protocol wants this for all new patients.',
    role: 'MA',
    owner: 'Kim Torres',
    due: '2026-02-03',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'edge',
    mustHave2026: true,
    evidence: {
      type: 'protocol-gap',
      summary:
        "APOE genotyping tells us the patient's genetic risk for ARIA if they go on treatment. Edge protocol wants this for all patients at first visit.",
      source: 'Mindspan Clinical Protocol — Edge Layer',
      sourceRef: 'EDGE-APOE-001',
    },
  },
  {
    id: 'T005',
    patientId: 'P001',
    team: 'member',
    title: 'Complete new patient welcome call',
    caption: 'Finish paperwork collection, insurance verification, and caregiver identification.',
    role: 'Care Navigator',
    owner: 'Jordan Mills',
    due: '2026-02-04',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Post-99205 protocol requires a welcome call within 24 hours: verify insurance, collect outstanding paperwork, and identify the primary caregiver for CDR and visit accompaniment.',
      source: 'Post-99205 Orchestration — Half A',
      sourceRef: 'WF1-WELCOME-001',
    },
  },

  // Half A: Safety sub-process
  {
    id: 'T006',
    patientId: 'P001',
    team: 'ops',
    title: 'Create driving safety plan — patient agreed to stop',
    caption:
      "Note says 'patient agrees to stop driving.' Need verification task + caregiver education.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-05',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    evidence: {
      type: 'safety-trigger',
      summary:
        "Dr. Patel's note documents a driving safety discussion: 'Patient agrees to stop driving, daughter will take keys.' Safety sub-process requires: (1) key surrender verification task, (2) caregiver education module, (3) follow-up checkpoint at Phase 2 visit.",
      source: 'Post-99205 Safety Action Sub-Process',
      sourceRef: 'WF1-SAFE-DRIVE-001',
    },
  },

  // Half B: Phase 2 prerequisite chain
  {
    id: 'T007',
    patientId: 'P001',
    team: 'ops',
    title: 'Schedule Cryos cognitive testing session',
    caption: 'Must be done at least 2 weeks before the 99483 visit so results are ready.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-06',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Cryos 12-domain cognitive battery is a prerequisite for Phase 2. Must be scheduled within 3 days of 99205 and completed by week 4 so results are scored before the 99483.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-CRYOS',
    },
  },
  {
    id: 'T008',
    patientId: 'P001',
    team: 'member',
    title: 'Send CDR questionnaire to caregiver',
    caption:
      'Caregiver must complete the Clinical Dementia Rating questionnaire before Phase 2 visit.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-07',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'CDR needs caregiver input. Must be sent within 3 days of identifying the primary caregiver and completed before the 99483 visit. Depends on welcome call (T005) to confirm caregiver contact.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-CDR',
    },
  },
  {
    id: 'T009',
    patientId: 'P001',
    team: 'ops',
    title: 'Schedule 99483 visit (~6 weeks out)',
    caption: 'Must be booked as 99483 + 96132, not a generic follow-up. Within the 6-week window.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-10',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'The Phase 2 visit must be booked with the correct visit type (99483 + 96132). A common failure mode is booking a generic follow-up instead, which wastes the slot. Target: ~6 weeks from today.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-SCHED',
    },
  },

  // ═══ PATIENT: David Kowalski (P002) — 99205 signed 3 DAYS AGO, partial progress ═══
  {
    id: 'T010',
    patientId: 'P002',
    team: 'ops',
    title: 'Track MRI results — ordered 1/31, not yet resulted',
    caption: "MRI was ordered at outside facility. Report hasn't arrived in Athena yet.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-07',
    priority: 'high',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'state-mismatch',
      summary:
        'MRI brain was ordered on 1/31 at an outside imaging center. The order exists in Athena but no report has arrived. External facility reports sometimes never make it into the chart — this is a top failure mode.',
      source: 'Post-99205 Orchestration — Result Tracking',
      sourceRef: 'WF1-TRACK-MRI',
    },
  },
  {
    id: 'T011',
    patientId: 'P002',
    team: 'ops',
    title: 'Schedule Cryos session (overdue — should have been done by 2/2)',
    caption: "Protocol says schedule within 3 days. We're now 1 day late.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        "Cryos scheduling should happen within 3 days of the 99205 (by 2/2). It's now 2/3 and no booking exists. Escalation rule triggered: prerequisite overdue >1 day.",
      source: 'Post-99205 Orchestration — Escalation Rules',
      sourceRef: 'WF1-ESC-CRYOS',
    },
  },
  {
    id: 'T012',
    patientId: 'P002',
    team: 'member',
    title: 'Send CDR questionnaire to wife',
    caption: 'Wife identified as primary caregiver during welcome call. CDR not sent yet.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-04',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Welcome call completed 2/1 — wife (Maria Kowalski) identified as primary caregiver. CDR instrument should have been sent within 24 hours of caregiver identification. Now 2 days late.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-CDR',
    },
  },
  {
    id: 'T013',
    patientId: 'P002',
    team: 'ops',
    title: 'Schedule 99483 visit — correct type (99483 + 96132)',
    caption: 'Follow-up is booked but as a generic visit. Needs to be changed to 99483 + 96132.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-05',
    priority: 'high',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'state-mismatch',
      summary:
        "A follow-up visit is scheduled for 3/12, but it's booked as a generic 99215 instead of 99483 + 96132. This is the #1 failure mode — wrong visit type means the cognitive assessment can't be billed or properly conducted.",
      source: 'Post-99205 Orchestration — Scheduling Reconciliation',
      sourceRef: 'WF1-SCHED-TYPE',
    },
  },

  // ═══ PATIENT: Linda Okonkwo (P003) — 99205 signed 1 WEEK AGO, most things done ═══
  {
    id: 'T014',
    patientId: 'P003',
    team: 'clinical',
    title: 'Record biological disease stage (ATN)',
    caption: 'Biomarker results are back. Dr. Chen needs to assign ATN stage before Phase 2.',
    role: 'Clinician',
    owner: 'Dr. Chen',
    due: '2026-02-07',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'edge',
    mustHave2026: true,
    evidence: {
      type: 'state-mismatch',
      summary:
        "Linda's biomarker results came back 2/1 (p-tau217 elevated, Aβ42/40 reduced). The ATN biological staging field in her record is still empty. Dr. Chen needs to assign it before Phase 2 so the care plan reflects the biology.",
      source: 'Mindspan Clinical Protocol — Edge Layer',
      sourceRef: 'EDGE-ATN-001',
    },
  },
  {
    id: 'T015',
    patientId: 'P003',
    team: 'member',
    title: 'Confirm son can attend 99483 visit on 2/24',
    caption:
      "Son is listed as primary caregiver. Day-before confirmation hasn't happened — visit is 3 weeks out.",
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-20',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Son (Michael Okonkwo) confirmed as primary caregiver. He needs to attend the 99483. Confirmation call is due at T-3 before the visit. This is on track but needs to be done.',
      source: 'Post-99205 Orchestration — Pre-Visit Gates',
      sourceRef: 'WF1-GATE-STAKEHOLDER',
    },
  },

  // ═══ PATIENT: Richard Huang (P004) — 99205 signed 2 WEEKS AGO, 99483 in 2 weeks ═══
  {
    id: 'T016',
    patientId: 'P004',
    team: 'ops',
    title: 'Chase lab results — drawn 1/22, still not resulted',
    caption: 'Dementia labs were drawn 12 days ago. Results should have been back in 5–7 days.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        "Dementia labs (B12, TSH, CMP, CBC) were drawn on 1/22 — that's 12 days ago. Standard turnaround is 5–7 days. Results are >5 days overdue. Escalation rule: overdue >7 days → alert coordinator + ops lead.",
      source: 'Post-99205 Orchestration — Result Tracking Escalation',
      sourceRef: 'WF1-ESC-LABS',
    },
  },
  {
    id: 'T017',
    patientId: 'P004',
    team: 'member',
    title: 'Confirm wife can attend 99483 on 2/17',
    caption: 'T-14 readiness gate is today. Caregiver attendance must be confirmed.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-05',
    priority: 'high',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'time-expiring',
      summary:
        "Richard's 99483 is on 2/17 — that's 14 days out. The T-14 pre-visit readiness sweep runs today. Caregiver (wife, Li-Mei Huang) must be confirmed for the visit. Contact was made 1/28 but no confirmation received.",
      source: 'Post-99205 Orchestration — Pre-Visit Gate T-14',
      sourceRef: 'WF1-GATE-T14',
    },
  },

  // ═══ PATIENT: Barbara Torres (P005) — 99205 signed 4 WEEKS AGO, 99483 in 2 DAYS ═══
  {
    id: 'T018',
    patientId: 'P005',
    team: 'ops',
    title: 'URGENT: Cryos testing still not completed',
    caption:
      '99483 is in 2 days. Cryos was scheduled for 1/24 but patient no-showed. Not rescheduled.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'blocked',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'gate-blocked',
      summary:
        "Barbara's 99483 is on 2/5 (T-2). Cryos was scheduled for 1/24 but she no-showed. It was never rescheduled. Without Cryos results, the 99483 visit cannot proceed as designed. At T-2 with a RED dimension, the clinician must be alerted for a possible reschedule.",
      source: 'Post-99205 Orchestration — Pre-Visit Gate T-2',
      sourceRef: 'WF1-GATE-T2-CRIT',
    },
  },
  {
    id: 'T019',
    patientId: 'P005',
    team: 'member',
    title: 'URGENT: CDR questionnaire not returned',
    caption: 'Sent to daughter on 1/10 — never completed. Visit is in 2 days.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        'CDR instrument was sent to daughter (Rosa Torres) on 1/10 — 24 days ago. Never completed. The 99483 visit requires this. At T-2, this triggers a critical escalation: alert clinician, consider reschedule.',
      source: 'Post-99205 Orchestration — Pre-Visit Gate T-2',
      sourceRef: 'WF1-GATE-T2-CDR',
    },
  },
  {
    id: 'T020',
    patientId: 'P005',
    team: 'clinical',
    title: 'Review readiness — decide: proceed or reschedule 99483',
    caption: 'Two RED dimensions at T-2: Cryos not done, CDR not returned. Clinician must decide.',
    role: 'Clinician',
    owner: 'Dr. Nakamura',
    due: '2026-02-03',
    priority: 'critical',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'gate-blocked',
      summary:
        "Barbara's 99483 visit (2/5) has 2 RED readiness dimensions at the T-2 gate: Assessments (Cryos no-show, not rescheduled) and Stakeholders (CDR not returned). Protocol: if any dimension is RED at T-2, alert clinician for reschedule decision. Labs and MRI are green.",
      source: 'Post-99205 Orchestration — T-2 Escalation to Clinician',
      sourceRef: 'WF1-ESC-T2-CLIN',
    },
  },

  // ═══ PATIENT: George Adams (P006) — 99205 signed 6 WEEKS AGO, stalled ═══
  {
    id: 'T021',
    patientId: 'P006',
    team: 'ops',
    title: 'CRITICAL DRIFT: Patient stalled 42 days in Phase 1',
    caption: 'No 99483 scheduled, MRI not done, Cryos not scheduled. 6-week window expiring.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        "George's 99205 was on 12/23/2025 — 42 days ago. The 6-week window for Phase 2 conversion is now expiring. Status: labs resulted (12/30), but MRI never ordered, Cryos never scheduled, 99483 never booked. This triggers a CRITICAL drift alert to clinician + ops lead.",
      source: 'Post-99205 Orchestration — Phase 1 Stall Detection',
      sourceRef: 'WF1-DRIFT-42DAY',
    },
  },
  {
    id: 'T022',
    patientId: 'P006',
    team: 'clinical',
    title: 'Review stalled patient — determine next steps',
    caption:
      'Patient dropped out of the pipeline for 6 weeks. Was the 99483 path abandoned or just lost?',
    role: 'Clinician',
    owner: 'Dr. Chen',
    due: '2026-02-04',
    priority: 'critical',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        'George has been in Phase 1 for 42 days with minimal progress. Dr. Chen needs to determine: was the 99483 pathway intentionally deferred (patient choice, comorbidity)? Or did this patient simply get lost in the handoff? If lost, immediate remediation is needed.',
      source: 'Post-99205 Orchestration — Drift Escalation to Clinician',
      sourceRef: 'WF1-DRIFT-CLIN',
    },
  },
];

export const APPOINTMENTS = [
  // Barbara Torres — 99483 in 2 days (crisis)
  {
    id: 'A001',
    patientId: 'P005',
    date: '2026-02-05',
    time: '09:00',
    visitType: '99483',
    clinician: 'Dr. Nakamura',
    location: 'Clinic Room 1',
    readiness: {
      evidence: {
        status: 'green',
        detail:
          'All test results are back: dementia labs resulted 1/15, MRI resulted 1/20. Biomarkers resulted 1/25 (Edge). APOE: e3/e3.',
      },
      assessments: {
        status: 'red',
        detail:
          'Cryos not completed — patient no-showed 1/24 appointment, never rescheduled. CDR questionnaire sent to daughter 1/10, never returned.',
        tasks: ['T018', 'T019'],
      },
      scheduling: {
        status: 'green',
        detail: '99483 + 96132 booked correctly. Within the 6-week window.',
      },
      stakeholders: {
        status: 'red',
        detail:
          "Daughter (Rosa Torres) hasn't confirmed attendance or completed CDR. No contact since 1/10.",
        tasks: ['T019'],
      },
    },
  },
  // Richard Huang — 99483 in 2 weeks (at risk)
  {
    id: 'A002',
    patientId: 'P004',
    date: '2026-02-17',
    time: '14:00',
    visitType: '99483',
    clinician: 'Dr. Patel',
    location: 'Clinic Room 2',
    readiness: {
      evidence: {
        status: 'red',
        detail:
          'Dementia labs drawn 1/22 but results still not back (12 days — 5+ days overdue). MRI resulted 1/28, clear. Biomarkers resulted 1/30 (Edge).',
        tasks: ['T016'],
      },
      assessments: {
        status: 'green',
        detail: 'Cryos completed 1/29. CDR completed by wife 1/31. MoCA from 1/20: 24.',
      },
      scheduling: {
        status: 'green',
        detail: '99483 + 96132 booked correctly. Within the 6-week window.',
      },
      stakeholders: {
        status: 'yellow',
        detail:
          'Wife contacted 1/28, verbal confirmation but not formally confirmed. T-14 gate check is today.',
        tasks: ['T017'],
      },
    },
  },
  // Linda Okonkwo — 99483 in 3 weeks (READY)
  {
    id: 'A003',
    patientId: 'P003',
    date: '2026-02-24',
    time: '10:00',
    visitType: '99483',
    clinician: 'Dr. Chen',
    location: 'Clinic Room 3',
    readiness: {
      evidence: {
        status: 'green',
        detail:
          'All results back: labs resulted 2/1, MRI resulted 1/31. Biomarkers resulted 2/1 (p-tau217 elevated). APOE: e3/e4. ATN stage recorded by Dr. Chen on 2/2.',
      },
      assessments: {
        status: 'green',
        detail: 'Cryos completed 1/30. CDR completed by son 2/1. MoCA from 1/27: 22.',
      },
      scheduling: {
        status: 'green',
        detail: '99483 + 96132 booked correctly. Within the 6-week window.',
      },
      stakeholders: {
        status: 'green',
        detail: 'Son (Michael Okonkwo) confirmed attendance for 2/24. Contact verified 2/2.',
      },
    },
  },
  // David Kowalski — 99483 in 5 weeks (early, issues building)
  {
    id: 'A004',
    patientId: 'P002',
    date: '2026-03-12',
    time: '11:00',
    visitType: '99483',
    clinician: 'Dr. Nakamura',
    location: 'Clinic Room 1',
    readiness: {
      evidence: {
        status: 'yellow',
        detail:
          'Dementia labs resulted 2/2. MRI ordered 1/31 at outside facility — report not received yet. Biomarkers ordered 1/31 (Edge). APOE ordered 1/31 (Edge).',
        tasks: ['T010'],
      },
      assessments: {
        status: 'red',
        detail: 'Cryos not scheduled (1 day overdue). CDR not sent to wife yet.',
        tasks: ['T011', 'T012'],
      },
      scheduling: {
        status: 'red',
        detail:
          'Visit is booked but as a generic 99215 — wrong visit type. Must be changed to 99483 + 96132.',
        tasks: ['T013'],
      },
      stakeholders: {
        status: 'yellow',
        detail:
          'Wife identified during welcome call. No visit confirmation yet (too early — visit is 5 weeks out).',
      },
    },
  },
  // Sarah Mitchell — 99483 not yet booked (just started today)
  {
    id: 'A005',
    patientId: 'P001',
    date: '2026-03-17',
    time: 'TBD',
    visitType: '99483',
    clinician: 'Dr. Patel',
    location: 'TBD',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    readiness: {
      evidence: {
        status: 'yellow',
        detail:
          "Dementia labs (B12, TSH, CMP, CBC) placed during visit — linked to tracking, awaiting results. MRI: note says 'order MRI brain with dementia protocol' but no imaging order or referral exists in Athena (gap). Blood biomarkers and APOE genotyping not ordered — Edge protocol requires both at Phase 1 (not mentioned in note).",
        tasks: ['T002', 'T003', 'T004'],
      },
      assessments: {
        status: 'red',
        detail:
          'Cryos not scheduled. CDR not sent — caregiver not yet identified (welcome call pending).',
        tasks: ['T007', 'T008'],
      },
      scheduling: {
        status: 'red',
        detail:
          '99483 visit not yet booked. Needs to be scheduled as 99483 + 96132 within the 6-week window.',
        tasks: ['T009'],
      },
      stakeholders: {
        status: 'red',
        detail: 'Caregiver not yet identified. Welcome call (today/tomorrow) will establish this.',
        tasks: ['T005'],
      },
    },
  },
];

export const pathwayLabel = {
  cognitive: 'Cognitive',
  kisunla: 'Kisunla',
  'leqembi-iv': 'Leqembi IV',
  'leqembi-sc': 'Leqembi SC',
};
export const teamMeta = {
  member: { label: 'Member Experience', icon: '○' },
  ops: { label: 'Operations', icon: '◎' },
  clinical: { label: 'Clinical', icon: '●' },
};

// ╔══════════════════════════════════════════════════════════════════════╗
// ║  END SCENARIO DATA — UI code below is scenario-independent.        ║
// ╚══════════════════════════════════════════════════════════════════════╝

// ─── HELPERS ───

export const statusColor = { green: '#22C55E', yellow: '#EAB308', red: '#EF4444' };
export const statusBg = { green: '#F0FDF4', yellow: '#FEFCE8', red: '#FEF2F2' };
export const statusBorder = { green: '#BBF7D0', yellow: '#FDE68A', red: '#FECACA' };
